import { useState } from "react";
import { ThumbsUp, ThumbsDown, Send, X } from "lucide-react";
import { cn } from "../lib/utils";
import { copilotApi } from "../hooks/useApi";

// ---------------------------------------------------------------------------
// Negative feedback reason options (match Streamlit prototype)
// ---------------------------------------------------------------------------

const FEEDBACK_REASONS = [
  { value: "incorrect", label: "Incorrect information" },
  { value: "irrelevant", label: "Not relevant" },
  { value: "missing", label: "Missing information" },
  { value: "other", label: "Other" },
] as const;

// ---------------------------------------------------------------------------
// ChatFeedback component
// ---------------------------------------------------------------------------

interface ChatFeedbackProps {
  logId: string;
  /** Pre-existing feedback value (1 or -1) — renders in "done" state. */
  feedbackState?: number;
  onFeedbackSubmitted?: (feedback: number) => void;
}

export function ChatFeedback({ logId, feedbackState, onFeedbackSubmitted }: ChatFeedbackProps) {
  const [state, setState] = useState<
    "idle" | "expanded" | "submitting" | "done"
  >(feedbackState !== undefined ? "done" : "idle");
  const [selectedFeedback, setSelectedFeedback] = useState<number | null>(
    feedbackState ?? null,
  );
  const [reason, setReason] = useState<string>("");
  const [feedbackText, setFeedbackText] = useState("");
  const isSubmitting = state === "submitting";

  const submitFeedback = async (
    feedback: number,
    feedbackReason?: string,
    text?: string,
  ) => {
    setState("submitting");
    try {
      await copilotApi.patch(`/copilot/logs/${logId}/feedback`, {
        feedback,
        feedback_reason: feedbackReason || null,
        feedback_text: text || null,
      });
      setSelectedFeedback(feedback);
      setState("done");
      onFeedbackSubmitted?.(feedback);
    } catch {
      // Silently fail — don't block the user's workflow
      setState("done");
    }
  };

  const handleThumbsUp = () => {
    setSelectedFeedback(1);
    setState("done");
    copilotApi
      .patch(`/copilot/logs/${logId}/feedback`, {
        feedback: 1,
        feedback_reason: null,
        feedback_text: null,
      })
      .then(() => onFeedbackSubmitted?.(1))
      .catch(() => {});
  };

  const handleThumbsDown = () => {
    setSelectedFeedback(-1);
    setState("expanded");
  };

  const handleSubmitNegative = () => {
    submitFeedback(-1, reason || undefined, feedbackText || undefined);
  };

  const handleCancel = () => {
    setState("idle");
    setReason("");
    setFeedbackText("");
  };

  if (state === "done") {
    return (
      <div className="mt-1 flex items-center gap-1 text-[10px] text-text-muted">
        {selectedFeedback === 1 ? (
          <ThumbsUp className="h-3 w-3 text-green-600 dark:text-green-400" />
        ) : (
          <ThumbsDown className="h-3 w-3 text-red-500 dark:text-red-400" />
        )}
        <span>Feedback recorded</span>
      </div>
    );
  }

  if (state === "expanded" || state === "submitting") {
    return (
      <div className="mt-2 max-w-[min(80%,32rem)] rounded-lg border border-border bg-surface-elevated p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-text-primary">
            What went wrong?
          </span>
          <button
            type="button"
            onClick={handleCancel}
            className="rounded p-0.5 text-text-muted hover:text-text-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Reason radio buttons */}
        <div className="space-y-1.5">
          {FEEDBACK_REASONS.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary"
            >
              <input
                type="radio"
                name="feedback-reason"
                value={opt.value}
                checked={reason === opt.value}
                onChange={() => setReason(opt.value)}
                className="h-3 w-3 accent-brand"
              />
              {opt.label}
            </label>
          ))}
        </div>

        {/* Optional text details */}
        <textarea
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder="Additional details (optional)"
          rows={2}
          className="mt-2 w-full resize-none rounded-md border border-border bg-surface-muted px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none"
        />

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmitNegative}
          disabled={isSubmitting}
          className={cn(
            "mt-2 inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium",
            "bg-brand text-text-inverse hover:bg-brand/90",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          <Send className="h-3 w-3" />
          Submit
        </button>
      </div>
    );
  }

  // Default: idle state — show thumbs buttons
  return (
    <div className="mt-1.5 flex items-center gap-3">
      <button
        type="button"
        onClick={handleThumbsUp}
        className="rounded-md p-1.5 text-text-muted hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-900/30 dark:hover:text-green-400"
        aria-label="Thumbs up"
      >
        <ThumbsUp className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={handleThumbsDown}
        className="rounded-md p-1.5 text-text-muted hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-400"
        aria-label="Thumbs down"
      >
        <ThumbsDown className="h-4 w-4" />
      </button>
    </div>
  );
}
